// ─── Export Composition Engine ─────────────────────────────────────────────────
// Single source of truth for thesis document composition.
//
// Composes the final document in strict academic order:
//   Title → Certificate → Declaration → Dedication → Acknowledgements →
//   Abstract → TOC → LOF → LOT → Chapters → References
//
// Figure X.Y / Table X.Y numbers are assigned ONLY by final document position —
// never by DB insertion order, generation order, or anchor creation order.
//
// Both the DOCX exporter and the HTML/preview exporter consume ComposedDocument.

import { sanitizeChapterContent } from './contentSanitizer.js'
import { inlineExportMermaid } from './export/mermaidRenderer.js'

// ── Token scanner (regex — works browser + SSR) ───────────────────────────────

const TOKEN_RE = /<div\s+data-thesis-figure="true"([^>]*)><\/div>/gi

/**
 * decodeURIComponent that never throws.
 *
 * Figure payloads are stored percent-encoded, but real content contains bare
 * "%" (percentages are everywhere in a quantitative thesis) and other sequences
 * that are not valid encoding. A single URIError here aborted the entire export
 * — no document, no download, just a failed promise — because one cell in one
 * table happened to contain a percent sign.
 *
 * Returning the input unchanged degrades one figure at worst instead of losing
 * the whole thesis.
 */
export function safeDecode(value) {
  const s = String(value ?? '')
  if (!s) return ''
  try {
    return decodeURIComponent(s)
  } catch {
    // Recover the well-formed parts; a stray "%" survives as itself.
    try {
      return s.replace(/%(?![0-9A-Fa-f]{2})/g, '%25')
        .split(/(%[0-9A-Fa-f]{2})/)
        .map(part => (/^%[0-9A-Fa-f]{2}$/.test(part) ? decodeURIComponent(part) : part))
        .join('')
    } catch {
      return s
    }
  }
}

function getAttr(str, name) {
  const m = str.match(new RegExp(`data-${name}="([^"]*)"`, 'i'))
  return m ? safeDecode(m[1]) : ''
}

function scanTokensInOrder(html) {
  if (!html) return []
  const items = []
  let m
  TOKEN_RE.lastIndex = 0
  while ((m = TOKEN_RE.exec(html)) !== null) {
    const a = m[1]
    items.push({
      token:   getAttr(a, 'figure-id'),
      type:    getAttr(a, 'figure-type') || 'mermaid',
      title:   getAttr(a, 'figure-title'),
      caption: getAttr(a, 'figure-caption'),
      code:    getAttr(a, 'figure-code'),
    })
  }
  return items
}

// ── H2 heading extractor for sub-TOC ─────────────────────────────────────────

function extractH2Headings(html) {
  if (!html) return []
  const headings = []
  const re = /<h2[^>]*>([\s\S]*?)<\/h2>/gi
  let m
  while ((m = re.exec(html)) !== null) {
    const text = m[1].replace(/<[^>]+>/g, '').trim()
    if (text) headings.push(text)
  }
  return headings
}

// ── Front matter canonical order ──────────────────────────────────────────────

export const FRONT_MATTER_ORDER = [
  'title',
  'certificate',
  'declaration',
  'dedication',
  'acknowledgements',
  'abstract',
]

const FRONT_MATTER_LABELS = {
  title:            'Title Page',
  certificate:      'Certificate',
  declaration:      'Declaration',
  dedication:       'Dedication',
  acknowledgements: 'Acknowledgements',
  abstract:         'Abstract',
}

// ── Main composition function ─────────────────────────────────────────────────

/**
 * Compose the complete thesis document in authoritative academic order.
 * Returns a ComposedDocument consumed by both DOCX and HTML exporters.
 *
 * @param {{
 *   thesis: object,
 *   chapters: Array,
 *   frontMatter?: object,   — key/value from FrontMatterPage
 * }} params
 *
 * @returns {ComposedDocument}
 */
export function composeThesisDocument({ thesis, chapters = [], frontMatter = {} }) {
  const sorted = [...chapters].sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0))

  const figureRegistry = new Map() // token → ComposedFigureEntry
  const tableRegistry  = new Map()
  const composedChapters = []
  const toc = []
  const lof = []
  const lot = []

  // ── Walk chapters in document order ────────────────────────────────────────
  for (const ch of sorted) {
    const chNum     = ch.chapter_number || 0
    const sanitized = sanitizeChapterContent(ch.content || '')
    const tokens    = scanTokensInOrder(sanitized)
    const h2s       = extractH2Headings(sanitized)

    let figSeq = 1
    let tblSeq = 1
    const chFigures = []
    const chTables  = []

    for (const tok of tokens) {
      if (!tok.token) continue // skip malformed tokens

      if (tok.type === 'table') {
        const num = `${chNum}.${tblSeq++}`
        const entry = {
          token:        tok.token,
          tableNumber:  num,
          displayLabel: `Table ${num}`,
          title:        stripLeadingFigureNumber(tok.title),
          caption:      tok.caption,
          code:         tok.code,
          type:         'table',
          chapterNumber: chNum,
          chapterId:    ch.id,
        }
        tableRegistry.set(tok.token, entry)
        chTables.push(entry)
        lot.push({ displayLabel: entry.displayLabel, title: entry.title, caption: entry.caption })
      } else {
        const num = `${chNum}.${figSeq++}`
        const entry = {
          token:        tok.token,
          figureNumber: num,
          displayLabel: `Figure ${num}`,
          title:        stripLeadingFigureNumber(tok.title),
          caption:      tok.caption,
          code:         tok.code,
          type:         tok.type,   // mermaid | chart-svg
          chapterNumber: chNum,
          chapterId:    ch.id,
        }
        figureRegistry.set(tok.token, entry)
        chFigures.push(entry)
        lof.push({ displayLabel: entry.displayLabel, title: entry.title, caption: entry.caption, figureType: tok.type })
      }
    }

    // Chapter TOC entry
    toc.push({ level: 1, chapterNumber: chNum, title: ch.title || `Chapter ${chNum}`, id: ch.id })
    // Sub-section TOC entries (H2 headings)
    h2s.forEach(h => toc.push({ level: 2, chapterNumber: chNum, title: h }))

    composedChapters.push({
      id:            ch.id,
      chapterNumber: chNum,
      title:         ch.title || '',
      sanitizedHtml: sanitized,
      figures:       chFigures,
      tables:        chTables,
      status:        ch.status,
      targetWords:   ch.target_words,
    })
  }

  return {
    thesis,
    frontMatter,
    chapters: composedChapters,
    figureRegistry,
    tableRegistry,
    toc,
    lof,
    lot,
    totalFigures: lof.length,
    totalTables:  lot.length,
    documentOrder: FRONT_MATTER_ORDER,
  }
}

// ── Figure token → HTML renderer ──────────────────────────────────────────────

function escapeHtml(s) {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * Remove a figure/table number the model wrote into the title.
 *
 * The generation prompt showed "TITLE: Figure 2.1 — Descriptive Title", so the
 * model authored the number itself. Each generation call is independent and has
 * no idea how many figures already exist, so every call restarted at X.1 — a
 * real chapter 4 contained two figures both titled "Figure 4.1". The export
 * prints its own correct label AND this title, so both numbers land on the page
 * and the reader sees a duplicate.
 *
 * Numbering is assigned at composition time; the title only carries the words.
 */
export function stripLeadingFigureNumber(title) {
  const t = String(title || '').trim()
  if (!t) return ''
  // "Figure 4.1 — Foo" / "Table 2.3: Foo" / "Fig. 1.2. Foo" → "Foo"
  const stripped = t.replace(/^(?:fig(?:ure)?|table|tbl)\.?\s*\d+(?:\.\d+)*\s*[—–\-:.]?\s*/i, '')
  // A title that was ONLY a number keeps its original text rather than becoming
  // an empty caption.
  return stripped.trim() || t
}

/**
 * Markdown pipe table → real HTML table, with the caption block the thesis
 * template expects. Rows with a different column count to the header are padded
 * or trimmed rather than dropped, so a ragged table still exports.
 */
export function renderMarkdownTable(code, label, title, caption) {
  const lines = String(code || '').trim().split('\n').map(l => l.trim()).filter(Boolean)
  const rows  = lines
    .filter(l => l.includes('|') && !/^\|?[\s:|-]+\|?$/.test(l))   // drop the |---|---| separator
    .map(l => l.replace(/^\||\|$/g, '').split('|').map(c => c.trim()))

  const head = `<div class="fig-number" style="font-weight:bold;font-size:10pt;margin-bottom:0.25rem">${escapeHtml(label)}</div>
  <div class="fig-title" style="font-weight:bold;font-size:10pt;margin-bottom:0.5rem">${escapeHtml(title || '')}</div>`
  const foot = caption
    ? `<div class="fig-caption" style="font-style:italic;font-size:9pt;color:#444;margin-top:0.25rem">${escapeHtml(caption)}</div>`
    : ''

  if (!rows.length) {
    return `<div class="thesis-fig thesis-fig--table" style="page-break-inside:avoid;margin:2rem 0">${head}${foot}</div>`
  }

  const cols = rows[0].length
  const cell = (c, tag) => `<${tag} style="border:1px solid #999;padding:4px 8px;text-align:left">${escapeHtml(c)}</${tag}>`
  const pad  = (r) => Array.from({ length: cols }, (_, i) => r[i] || '')

  const thead = `<thead><tr>${pad(rows[0]).map(c => cell(c, 'th')).join('')}</tr></thead>`
  const tbody = rows.length > 1
    ? `<tbody>${rows.slice(1).map(r => `<tr>${pad(r).map(c => cell(c, 'td')).join('')}</tr>`).join('')}</tbody>`
    : ''

  return `<div class="thesis-fig thesis-fig--table" style="page-break-inside:avoid;margin:2rem 0">
  ${head}
  <table style="border-collapse:collapse;width:100%;font-size:10pt">${thead}${tbody}</table>
  ${foot}
</div>`
}

/**
 * Replace data-thesis-figure tokens in chapter HTML with rendered figure blocks.
 * Uses the authoritative registries from composeThesisDocument for labelling.
 *
 * mode 'preview' — compact labelled box (no mermaid, no SVG)
 * mode 'print'   — full inline SVG for chart-svg; mermaid pre block for diagrams
 */
export function renderComposedChapterHtml(html, figureRegistry, tableRegistry, mode = 'preview', ordered = null) {
  if (!html) return ''
  TOKEN_RE.lastIndex = 0

  // Token ids are NOT unique. They are generated as fig_<chapterNumber>_<n>, and
  // the generator defaults chapterNumber to 1 whenever the chapter record has
  // not loaded yet — so a real thesis had 122 tokens sharing only 33 ids, with
  // "fig_1_1" appearing 8 times. Looking those up in a Map keyed by id returns
  // whichever duplicate was registered last, which mislabels figures throughout
  // the document and fills the List of Figures with wrong numbers.
  //
  // When the caller passes this chapter's ordered figure/table lists, tokens are
  // matched by position instead, which cannot collide. Registry lookup stays as
  // the fallback for callers that have no ordering (the preview page).
  let figSeq = 0
  let tblSeq = 0

  return html.replace(TOKEN_RE, (_, attrs) => {
    const token   = getAttr(attrs, 'figure-id')
    const type    = getAttr(attrs, 'figure-type')
    const rawTitle   = getAttr(attrs, 'figure-title')
    const rawCaption = getAttr(attrs, 'figure-caption')
    const rawCode    = getAttr(attrs, 'figure-code')

    const isTable = type === 'table'
    const positional = ordered
      ? (isTable ? ordered.tables?.[tblSeq++] : ordered.figures?.[figSeq++])
      : null
    const reg     = positional || (isTable ? tableRegistry.get(token) : figureRegistry.get(token))
    const label   = reg?.displayLabel || (isTable ? 'Table' : 'Figure')
    const title   = reg?.title   || stripLeadingFigureNumber(rawTitle) || 'Untitled'
    const caption = reg?.caption || rawCaption || ''

    if (mode === 'print') {
      if (type === 'chart-svg') {
        const svgContent = rawCode || ''   // already decoded by getAttr
        return `<div class="thesis-fig thesis-fig--svg" style="page-break-inside:avoid;margin:2rem 0;text-align:center">
  <div class="fig-number" style="font-weight:bold;font-size:10pt;margin-bottom:0.5rem">${escapeHtml(label)}</div>
  ${svgContent}
  <div class="fig-title" style="font-weight:bold;font-size:10pt;margin-top:0.5rem">${escapeHtml(title)}</div>
  ${caption ? `<div class="fig-caption" style="font-style:italic;font-size:9pt;color:#444;margin-top:0.25rem">${escapeHtml(caption)}</div>` : ''}
</div>`
      }
      if (type === 'mermaid') {
        const mermaidCode = rawCode || ''  // already decoded by getAttr
        return `<div class="thesis-fig thesis-fig--mermaid" style="page-break-inside:avoid;margin:2rem 0;text-align:center">
  <div class="fig-number" style="font-weight:bold;font-size:10pt;margin-bottom:0.5rem">${escapeHtml(label)}</div>
  <pre class="mermaid" style="text-align:left;background:#f8f6f2;border:1px solid #ccc;padding:0.75rem;border-radius:4px;font-size:8.5pt">${escapeHtml(mermaidCode)}</pre>
  <div class="fig-title" style="font-weight:bold;font-size:10pt;margin-top:0.5rem">${escapeHtml(title)}</div>
  ${caption ? `<div class="fig-caption" style="font-style:italic;font-size:9pt;color:#444;margin-top:0.25rem">${escapeHtml(caption)}</div>` : ''}
</div>`
      }
      if (type === 'table') {
        // This used to `return ''` on the theory that the table HTML was already
        // inline. It is not: tables live as markdown inside the token's
        // data-figure-code, so every table was silently deleted from the HTML
        // export while still being counted in the List of Tables.
        return renderMarkdownTable(rawCode || '', label, title, caption)
      }
      return ''
    }

    // preview mode — compact box
    return `<div class="thesis-fig figure-preview-box" style="border:1px solid #ccc;border-radius:4px;padding:0.75rem;background:#f8f6f2;margin:1.25rem 0;text-align:center;page-break-inside:avoid">
  <div style="font-weight:bold;font-size:10pt;color:#1e3a5f">[${escapeHtml(label)}: ${escapeHtml(title)}]</div>
  ${caption ? `<div style="font-style:italic;font-size:9pt;color:#666;margin-top:4px">${escapeHtml(caption)}</div>` : ''}
</div>`
  })
}

// ── Print CSS ─────────────────────────────────────────────────────────────────

export const PRINT_CSS = `
@page { size: A4; margin: 1in 1in 1in 1.5in; }
*, *::before, *::after { box-sizing: border-box; }
body { font-family: "Times New Roman", Georgia, serif; font-size: 12pt; line-height: 2; color: #000; margin: 0; }
.fm-page { page-break-after: always; min-height: calc(297mm - 2in); padding: 0.5in 0; }
h1.chapter-heading { font-family: "Times New Roman", Georgia, serif; font-size: 14pt; font-weight: bold; text-align: center; text-transform: uppercase; margin: 0 0 2rem; page-break-before: always; page-break-after: avoid; }
h2 { font-size: 12pt; font-weight: bold; margin: 1.5rem 0 0.75rem; page-break-after: avoid; }
h3 { font-size: 12pt; font-weight: bold; font-style: italic; margin: 1.25rem 0 0.5rem; page-break-after: avoid; }
h4 { font-size: 12pt; font-weight: bold; margin: 1rem 0 0.5rem; page-break-after: avoid; }
p  { margin: 0 0 0.5em; text-align: justify; text-indent: 0.5in; }
p:first-child { text-indent: 0; }
table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; font-size: 11pt; page-break-inside: avoid; }
th { background: #f0ece4; font-weight: bold; border: 1px solid #999; padding: 6px 10px; }
td { border: 1px solid #999; padding: 6px 10px; }
thead { display: table-header-group; }
blockquote { border-left: 3px solid #555; padding-left: 1rem; margin: 1rem 2rem; font-style: italic; color: #333; }
ul, ol { padding-left: 1.5rem; margin: 0 0 1em; }
li { margin-bottom: 0.25em; }
.thesis-fig { page-break-inside: avoid; max-width: 100%; }
/* Figures used to overrun the right margin: diagrams carry explicit pixel
   dimensions, so without an upper bound an oversized SVG simply hangs off the
   page. These rules cap anything the sizing pass missed. */
.thesis-fig svg,
.thesis-fig img   { max-width: 100%; height: auto; display: block; margin-left: auto; margin-right: auto; }
.thesis-fig table { max-width: 100%; table-layout: fixed; word-wrap: break-word; }
.lof-entry { display: flex; align-items: baseline; margin-bottom: 0.4rem; gap: 0.25rem; }
.lof-label { white-space: nowrap; min-width: 90px; font-style: italic; color: #333; }
.lof-title { flex: 0 1 auto; }
.lof-dots  { flex: 1; border-bottom: 1px dotted #666; margin: 0 6px; min-width: 30px; }
.toc-entry { margin-bottom: 0.4rem; }
.toc-chapter { font-weight: bold; }
.toc-section { margin-left: 1.5rem; font-weight: normal; }
`

// ── Full printable HTML builder ───────────────────────────────────────────────

/**
 * Build a complete printable HTML document from a ComposedDocument.
 * Suitable for browser-print or PDF save via print dialog.
 *
 * @param {{ composedDoc, profileHtml?: string }} params
 *   profileHtml — pre-rendered front matter HTML from universityProfiles
 * @returns {string} full HTML document
 */
export async function buildPrintableHtml({ composedDoc, profileHtml = '' }) {
  const { thesis, chapters, figureRegistry, tableRegistry, toc, lof, lot } = composedDoc

  const built = []
  for (const ch of chapters) {
    let rendered = renderComposedChapterHtml(ch.sanitizedHtml, figureRegistry, tableRegistry, 'print', { figures: ch.figures, tables: ch.tables })
    rendered = await inlineExportMermaid(rendered)
    built.push(`<div class="chapter" style="page-break-before:always">
  <h1 class="chapter-heading">CHAPTER ${ch.chapterNumber}: ${ch.title}</h1>
  ${rendered}
</div>`)
  }
  const chaptersHtml = built.join('\n')

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHtml(thesis?.title || 'PhD Thesis')}</title>
<meta name="viewport" content="width=device-width,initial-scale=1">
<style>${PRINT_CSS}</style>
</head>
<body>
${profileHtml}
${buildTocHtml(toc, lof.length > 0, lot.length > 0)}
${buildLofHtml(lof)}
${buildLotHtml(lot)}
${chaptersHtml}
</body>
</html>`
}

function buildTocHtml(toc, hasFigs, hasTables) {
  const chEntries = toc.filter(e => e.level === 1)
  const entries = chEntries.map(e => {
    const subs = toc.filter(s => s.level === 2 && s.chapterNumber === e.chapterNumber)
    return `<div class="toc-entry toc-chapter">Chapter ${e.chapterNumber}: ${escapeHtml(e.title)}</div>
${subs.map(s => `<div class="toc-entry toc-section">${escapeHtml(s.title)}</div>`).join('\n')}`
  }).join('\n')

  return `<div class="fm-page" style="page-break-before:always">
<h1 style="text-align:center;font-size:14pt;font-weight:bold;text-transform:uppercase;margin-bottom:2rem">Table of Contents</h1>
<div class="toc-entry toc-chapter">Certificate</div>
<div class="toc-entry toc-chapter">Declaration</div>
<div class="toc-entry toc-chapter">Dedication</div>
<div class="toc-entry toc-chapter">Acknowledgements</div>
<div class="toc-entry toc-chapter">Abstract</div>
${hasFigs ? '<div class="toc-entry toc-chapter">List of Figures</div>' : ''}
${hasTables ? '<div class="toc-entry toc-chapter">List of Tables</div>' : ''}
${entries}
<div class="toc-entry toc-chapter" style="margin-top:0.75rem">References / Bibliography</div>
</div>`
}

function buildLofHtml(lof) {
  if (!lof.length) return ''
  return `<div class="fm-page" style="page-break-before:always">
<h1 style="text-align:center;font-size:14pt;font-weight:bold;text-transform:uppercase;margin-bottom:2rem">List of Figures</h1>
${lof.map(f => `<div class="lof-entry">
  <span class="lof-label">${escapeHtml(f.displayLabel)}</span>
  <span class="lof-title">${escapeHtml(f.title || '')}</span>
  <span class="lof-dots"></span>
</div>`).join('\n')}
</div>`
}

function buildLotHtml(lot) {
  if (!lot.length) return ''
  return `<div class="fm-page" style="page-break-before:always">
<h1 style="text-align:center;font-size:14pt;font-weight:bold;text-transform:uppercase;margin-bottom:2rem">List of Tables</h1>
${lot.map(t => `<div class="lof-entry">
  <span class="lof-label">${escapeHtml(t.displayLabel)}</span>
  <span class="lof-title">${escapeHtml(t.title || '')}</span>
  <span class="lof-dots"></span>
</div>`).join('\n')}
</div>`
}
